import { Filter, X } from "lucide-react";
import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { BaseNavigationBar } from "@/lib/components/navigation";
import { PageContent } from "@/lib/components/page-content";
import { useAuthStore } from "@/lib/store/auth-enhanced";
import { AuditLogTimeline } from "../components";

export const AuditPage: React.FC = () => {
  const { user } = useAuthStore();
  const [dateRange, setDateRange] = useState<{
    from: string;
    to: string;
  }>({
    from: "",
    to: "",
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  if (!user?.role.some((r) => r === "ADMIN" || r === "SUPER_ADMIN")) {
    return <Navigate to="/appointments" replace />;
  }

  const clearDateFilter = () => {
    setDateRange({ from: "", to: "" });
  };

  const hasActiveFilters = dateRange.from || dateRange.to;
  const filterCount = (dateRange.from ? 1 : 0) + (dateRange.to ? 1 : 0);

  // Mobile Filter Button Component
  const MobileFilterButton = () => (
    <Sheet open={isFilterOpen} onOpenChange={setIsFilterOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm" className="relative">
          <Filter className="h-4 w-4" />
          {hasActiveFilters && (
            <Badge
              variant="destructive"
              className="absolute -top-2 -right-2 h-5 w-5 p-0 flex items-center justify-center text-xs"
            >
              {filterCount}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent side="bottom" className="h-auto">
        <SheetHeader>
          <SheetTitle>Filter Audit Logs</SheetTitle>
        </SheetHeader>
        <div className="space-y-4 mt-4">
          <div>
            <Label htmlFor="mobile-date-from" className="text-sm mb-1.5 block">
              From Date
            </Label>
            <Input
              id="mobile-date-from"
              type="date"
              value={dateRange.from}
              onChange={(e) => setDateRange((prev) => ({ ...prev, from: e.target.value }))}
              className="w-full"
            />
          </div>
          <div>
            <Label htmlFor="mobile-date-to" className="text-sm mb-1.5 block">
              To Date
            </Label>
            <Input
              id="mobile-date-to"
              type="date"
              value={dateRange.to}
              onChange={(e) => setDateRange((prev) => ({ ...prev, to: e.target.value }))}
              className="w-full"
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              onClick={clearDateFilter}
              className="flex-1"
              disabled={!hasActiveFilters}
            >
              Clear Filters
            </Button>
            <Button onClick={() => setIsFilterOpen(false)} className="flex-1">
              Apply Filters
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );

  // Desktop Filter Component (for navbar)
  const DesktopDateFilter = () => (
    <div className="flex gap-4">
      <div className="flex-1 sm:flex-initial">
        <Label htmlFor="desktop-date-from" className="text-sm mb-1.5 block">
          From Date
        </Label>
        <Input
          id="desktop-date-from"
          type="date"
          value={dateRange.from}
          onChange={(e) => setDateRange((prev) => ({ ...prev, from: e.target.value }))}
          className="w-full bg-background"
        />
      </div>
      <div className="flex-1 sm:flex-initial">
        <Label htmlFor="desktop-date-to" className="text-sm mb-1.5 block">
          To Date
        </Label>
        <Input
          id="desktop-date-to"
          type="date"
          value={dateRange.to}
          onChange={(e) => setDateRange((prev) => ({ ...prev, to: e.target.value }))}
          className="w-full bg-background"
        />
      </div>
      {hasActiveFilters && (
        <div className="flex items-end">
          <Button variant="ghost" size="sm" onClick={clearDateFilter}>
            Clear Filter
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <>
      <BaseNavigationBar
        title="Audit Log"
        subtitle="Track all changes and activities in your salon management system"
        trailingElements={[
          <div key="mobile-filter" className="sm:hidden">
            <MobileFilterButton />
          </div>,
        ]}
        bottomElement={
          <div className="hidden sm:block">
            <DesktopDateFilter />
          </div>
        }
      />

      {/* Mobile: Show active filters under navbar */}
      <div className="sm:hidden">
        {hasActiveFilters && (
          <div className="px-2 py-2">
            <Card>
              <CardContent className="py-2 px-3">
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground text-xs">Active filters:</span>
                  <div className="flex flex-wrap gap-1.5 flex-1">
                    {dateRange.from && (
                      <Badge variant="secondary" className="text-xs">
                        From: {new Date(dateRange.from).toLocaleDateString()}
                      </Badge>
                    )}
                    {dateRange.to && (
                      <Badge variant="secondary" className="text-xs">
                        To: {new Date(dateRange.to).toLocaleDateString()}
                      </Badge>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearDateFilter}
                    className="h-6 w-6 p-0 text-xs ml-auto"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      <PageContent>
        <AuditLogTimeline dateRange={dateRange} />
      </PageContent>
    </>
  );
};

export default AuditPage;
